// Participant ("external") session store: the meeting-scoped access +
// refresh JWT pair minted by admin/join, checkJoinStatus, sync, approve,
// promote and host/room-token. Persisted in sessionStorage keyed by
// meeting code so the axios layer can refresh access tokens single-flight
// and the socket can re-authenticate after a rotation.

const KEY_PREFIX = "vc_ext_session_";
const subscribers = new Set();

function decodeJwtPayload(token) {
  try {
    const part = String(token).split(".")[1] || "";
    const json = atob(part.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json);
  } catch (err) {
    return null;
  }
}

function computeExpiry(token, fallbackMin) {
  const payload = decodeJwtPayload(token);
  const exp = payload && payload.exp ? Number(payload.exp) * 1000 : null;
  return exp || (fallbackMin ? Date.now() + fallbackMin * 60 * 1000 : null);
}

function readSession(meetingCode) {
  try {
    const raw = sessionStorage.getItem(KEY_PREFIX + meetingCode);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    return null;
  }
}

function writeSession(meetingCode, session) {
  try {
    sessionStorage.setItem(KEY_PREFIX + meetingCode, JSON.stringify(session));
  } catch (err) {}
}

function notifySubscribers(meetingCode, session) {
  subscribers.forEach((fn) => {
    try {
      fn(meetingCode, session);
    } catch (err) {}
  });
}

// Store (or refresh) the token pair for a meeting. `token` is always the
// external ACCESS token; `refreshExternalToken` (optional) is the rotating
// refresh token. Any undefined meta is carried over from the prior session.
export function adoptExternalSession({
  meetingCode,
  token,
  refreshExternalToken,
  identity,
  phoneNumber,
  meetingId,
  useParticipantAuth,
}) {
  if (!meetingCode || !token) return null;
  const existing = readSession(meetingCode) || {};
  const session = {
    ...existing,
    meetingCode,
    accessToken: token,
    refreshToken: refreshExternalToken || null,
    accessExpiresAt: computeExpiry(token),
    refreshExpiresAt: refreshExternalToken
      ? computeExpiry(refreshExternalToken)
      : null,
    identity: identity || existing.identity || null,
    phoneNumber: phoneNumber || existing.phoneNumber || null,
    meetingId:
      meetingId !== undefined && meetingId !== null
        ? meetingId
        : existing.meetingId || null,
    useParticipantAuth:
      useParticipantAuth !== undefined
        ? useParticipantAuth
        : existing.useParticipantAuth,
    updatedAt: Date.now(),
  };
  writeSession(meetingCode, session);
  notifySubscribers(meetingCode, session);
  return session;
}

export function getExternalSession(meetingCode) {
  return meetingCode ? readSession(meetingCode) : null;
}

export function getExternalAccessToken(meetingCode) {
  const session = readSession(meetingCode);
  return session ? session.accessToken || null : null;
}

export function getExternalRefreshToken(meetingCode) {
  const session = readSession(meetingCode);
  return session ? session.refreshToken || null : null;
}

export function listExternalSessions() {
  const sessions = [];
  try {
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(KEY_PREFIX)) {
        const session = readSession(key.slice(KEY_PREFIX.length));
        if (session) sessions.push(session);
      }
    }
  } catch (err) {}
  return sessions;
}

export function clearExternalSession(meetingCode) {
  if (!meetingCode) return;
  try {
    sessionStorage.removeItem(KEY_PREFIX + meetingCode);
  } catch (err) {}
  notifySubscribers(meetingCode, null);
}

// Register a handler that runs whenever a session is adopted/refreshed
// (session) or cleared (null). Returns an unsubscribe function.
export function subscribeToExternalSessionUpdates(fn) {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
}